import { computed, defineComponent, ref, unref } from 'vue'
import type { ITreeV2Props } from './types'
import { Grid } from '../grid'
import ChevronIcon from 'assets/img/svg/icons/chevron_left.svg?url'
import Icon from '../icon'
import type { IBaseComponentProps } from '../base/types'
import type { TTree } from '../tree/types'
import Bubbletip from '../bubbletip'
import { useElementVisibility } from '@vueuse/core'

const SQUARE_SIZE = 7.5 * 2
const SQUARE_ICON_SIZE = SQUARE_SIZE * 0.69

const VerticalLine = defineComponent<{
  selected: boolean;
  canExpand: boolean;
}>(
  (props) => {
    const lineLeftColor = computed(() => (props.selected ? '#3F83F8' : '#D1D5DB'))
    return () => {
      return (
        <div
          style={{
            height: '100%',
            width: '2px',
            position: 'absolute',
            top: 0,
            left: props.canExpand ? '0px' : '-1px',
            background: `linear-gradient(to bottom, ${lineLeftColor.value}, transparent)`
          }}
        />
      )
    }
  },
  { props: ['selected', 'canExpand'] }
)

type ISquareProps = IBaseComponentProps & {
  node: TTree
  expanded?: boolean
  canExpand?: boolean
  selected?: boolean
}

const Square = defineComponent<ISquareProps>(
  (props) => {
    return () => {
      //const bgColor = (props.expanded && props.canExpand) || props.selected ? '#3F83F8' : '#C3DDFD'

      return (
        <div
          class="bg-findex-500 text-white flex-none"
          style={{
            width: props.node.children && props.node.children.length > 0 ? '1rem' : '0.5rem',
            marginLeft:
              props.node.children && props.node.children.length > 0 ? '-0.5rem' : '-0.25rem',
            aspectRatio: '1 / 1',
            overflow: 'hidden',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 2,
            ...(props.canExpand
              ? {
                  cursor: 'pointer'
                }
              : {})
          }}
        >
          {props.node.children && props.node.children.length > 0 ? (
            <Icon
              src={ChevronIcon}
              rotationDeg={props.expanded ? -90 : 0}
              flipHori={true}
              fill={'currentColor'}
              size={SQUARE_ICON_SIZE}
            />
          ) : (
            <div />
          )}
        </div>
      )
    }
  },
  { props: ['expanded', 'canExpand', 'selected', 'node'] }
)

export const TreeV2 = defineComponent<ITreeV2Props>(
  (props) => {
    const treeNode = computed(() => unref(props.node));
    const expanded = ref<boolean>(treeNode.value.expanded || treeNode.value.id === props.expandedId || false)
    const children = computed(() => treeNode.value.children || [])
    const depth = computed(() => props.depth || 0)
    const canExpand = computed(() => children.value.length > 0)

    const setExpanded = (exp: boolean) => {
      expanded.value = exp
    }

    const toggleExpanded = () => {
      setExpanded(!expanded.value)
    }

    const handleClick = () => {
      toggleExpanded()
    }

    const itemRef = ref();
    const wrapperRef = ref();

    const visible = useElementVisibility(wrapperRef);

    return () => {
      return (
        <div class="w-full h-full relative select-none">
          <div class="w-full space-y-3 relative">
            <Grid
              noDefaultLayout
              columns="0.5rem max-content"
              onClick={handleClick}
              class="w-full relative min-h-[1.6rem]"
              alignItems="center"
              gap="0.75rem"
              ref={wrapperRef}
            >
              <Square
                node={treeNode.value}
                canExpand={canExpand.value}
                expanded={expanded.value}
                selected={false}
              />
              <div class="relative" ref={itemRef} style={{ display: 'inline-block', }}>
                {treeNode.value.body ? (
                  <treeNode.value.body expanded={expanded.value} treeNode={treeNode.value} />
                ) : (
                  <div>{treeNode.value.name}</div>
                )}
                {treeNode.value.tooltip && visible.value &&
                  <Bubbletip {...treeNode.value.tooltip} triggerRef={itemRef} />
                }
              </div>
            </Grid>
            <VerticalLine selected={false} canExpand={canExpand.value} />
            {children.value.length > 0 && expanded.value && (
              <div
                class="w-full space-y-3 relative"
                style={{
                  paddingLeft: `1.9rem`
                }}
              >
                {children.value.map((child) => {
                  return <TreeV2 node={child} depth={depth.value + 1} expandedId={props.expandedId} />
                })}
              </div>
            )}
          </div>
        </div>
      )
    }
  },
  { props: ['node', 'depth', 'expandedId'] }
)
